<template>
  <div v-if="editor" class="tiptap-editor">
    <div class="tiptap-toolbar">
      <button @click="editor.chain().focus().toggleHeading({ level: 2 }).run()" :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }">
        H2
      </button>
      <button @click="editor.chain().focus().toggleHeading({ level: 3 }).run()" :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }">
        H3
      </button>
      <button @click="editor.chain().focus().toggleBulletList().run()" :class="{ 'is-active': editor.isActive('bulletList') }">
        Bullet List
      </button>
      <button @click="editor.chain().focus().toggleOrderedList().run()" :class="{ 'is-active': editor.isActive('orderedList') }">
        Ordered List
      </button>
      <button @click="setLink" :class="{ 'is-active': editor.isActive('link') }">
        Link
      </button>
      <button @click="addImage">
        Image
      </button>
      <button @click="editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()">
        Table
      </button>
      <button @click="editor.chain().focus().addRowAfter().run()">
        Add Row After
      </button>
      <button @click="editor.chain().focus().deleteRow().run()">
        Delete Row
      </button>
      <button @click="editor.chain().focus().addColumnAfter().run()">
        Add Col After
      </button>
      <button @click="editor.chain().focus().deleteColumn().run()">
        Delete Col
      </button>
      <button @click="editor.chain().focus().undo().run()">
        Undo
      </button>
      <button @click="editor.chain().focus().redo().run()">
        Redo
      </button>
    </div>

    <button @click="toggleHtmlMode" class="toggle-html-mode">
      {{ htmlMode ? 'Visual Mode' : 'HTML Mode' }}
    </button>

    <div v-if="htmlMode">
      <textarea v-model="htmlContent" @input="updateVisualContent" class="html-source-editor"></textarea>
    </div>
    <div v-else>
      <editor-content :editor="editor" class="tiptap-editor-content" />
    </div>

    <input type="file" ref="fileInput" @change="handleFileUpload" accept="image/*" style="display: none;">
    <div v-if="uploadMessage" class="upload-status">
      <p>{{ uploadMessage }}</p>
      <div v-if="uploadProgress > 0 && uploadProgress < 100" class="upload-progress-bar" :style="{ width: uploadProgress + '%' }"></div>
    </div>
  </div>
</template>

<script>
import { ref, watch, onBeforeUnmount } from 'vue';
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import DOMPurify from 'dompurify';
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getApp } from 'firebase/app'; // Needed to get the initialized Firebase app


export default {
  components: { EditorContent },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const htmlMode = ref(false);
    const htmlContent = ref(props.modelValue);

    // State for image upload
    const fileInput = ref(null); // Reference to the hidden file input
    const uploadProgress = ref(0);
    const uploadMessage = ref('');
    const firebaseApp = getApp(); // Get the default Firebase app instance
    const storage = getStorage(firebaseApp); // Get Firebase Storage instance

    const editor = useEditor({
      content: props.modelValue,
      extensions: [
        StarterKit,
        Link.configure({
          openOnClick: false,
          autolink: true,
          // Add keyboard shortcut for Link (Ctrl+L)
          keyboardShortcuts: {
            'Mod-l': () => setLink(), // Mod-l is Cmd-l on Mac, Ctrl-l on Windows
          },
        }),
        Image.configure({
          inline: true,
          allowBase64: true, // Allow pasting base64 images if needed
          // Add keyboard shortcut for Image (Ctrl+I)
          keyboardShortcuts: {
            'Mod-i': () => chooseImageSource(), // Mod-i is Cmd-i on Mac, Ctrl-i on Windows
          },
        }),
        Table.configure({
          resizable: true,
        }),
        TableRow,
        TableHeader,
        TableCell,
      ],
      onUpdate: ({ editor }) => {
        if (!htmlMode.value) { // Only update modelValue if in visual mode
          emit('update:modelValue', editor.getHTML());
        }
      },
      onBlur: ({ editor }) => {
        if (!htmlMode.value) { // Ensure modelValue is up-to-date on blur from visual mode
          emit('update:modelValue', editor.getHTML());
        }
      },
    });

    const toggleHtmlMode = () => {
      htmlMode.value = !htmlMode.value;
      if (htmlMode.value) {
        // Switching to HTML mode, update textarea with current editor HTML
        htmlContent.value = editor.value ? editor.value.getHTML() : '';
      } else {
        // Switching to Visual mode, update editor with sanitized HTML from textarea
        const sanitizedHtml = DOMPurify.sanitize(htmlContent.value, {
            USE_PROFILES: { html: true } // Allow standard HTML tags
        });
        editor.value.commands.setContent(sanitizedHtml, true);
      }
    };

    const updateVisualContent = () => {
      // This is called when textarea content changes in HTML mode
      // Don't emit modelValue yet, wait until switching back to visual mode
      // or rely on explicit save in parent
    };

    const setLink = () => {
      const previousUrl = editor.value.getAttributes('link').href;
      const url = window.prompt('URL', previousUrl);
    
      // cancelled
      if (url === null) {
        return;
      }
    
      // empty string as URL means unset the link
      if (url === '') {
        editor.value.chain().focus().unsetLink().run();
        return;
      }
    
      // update link
      editor.value.chain().focus().setLink({ href: url }).run();
    };

    // Function to handle image upload
    const uploadImage = async (file) => {
      if (!file) return;

      uploadMessage.value = 'Uploading...';
      uploadProgress.value = 0;

      const fileName = `${Date.now()}-${file.name}`;
      const storageReference = storageRef(storage, `images/${fileName}`);
      const uploadTask = uploadBytesResumable(storageReference, file);

      uploadTask.on('state_changed', 
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          uploadProgress.value = progress;
        }, 
        (error) => {
          uploadMessage.value = `Upload failed: ${error.message}`;
          console.error('Upload failed', error);
        }, 
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          editor.value.chain().focus().setImage({ src: downloadURL }).run();
          uploadMessage.value = 'Upload successful!';
          uploadProgress.value = 0;
        }
      );
    };

    // Handle file input change event
    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            uploadImage(file);
        }
    };

    // Modified addImage to present choice
    const chooseImageSource = () => {
        const choice = window.prompt('How do you want to add an image? (1: Upload, 2: From URL)', '1');
        if (choice === '1') {
            fileInput.value.click(); // Trigger hidden file input
        } else if (choice === '2') {
            const url = window.prompt('Enter the direct URL of the image (e.g., ends in .jpg, .png, .gif):');
            if (url) {
                const imageRegex = /\.(jpeg|jpg|gif|png|webp|svg)$/i;
                if (!imageRegex.test(url)) {
                    if (!confirm('The URL does not appear to be a direct image link. Do you still want to insert it?')) {
                        return;
                    }
                }
                editor.value.chain().focus().setImage({ src: url }).run();
            }
        }
    };
    // Watch for changes in modelValue from parent and update editor content
    watch(() => props.modelValue, (newValue) => {
      if (editor.value && newValue !== editor.value.getHTML()) {
        // Only update if not in HTML mode and content is different
        if (!htmlMode.value) {
            const sanitizedHtml = DOMPurify.sanitize(newValue, {
                USE_PROFILES: { html: true }
            });
            editor.value.commands.setContent(sanitizedHtml, false); // false to not emit update
        } else {
            htmlContent.value = newValue; // Update textarea directly
        }
      }
    });

    // Sync htmlContent with modelValue when in HTML mode, without triggering visual editor update
    watch(htmlContent, (newValue) => {
      if (htmlMode.value) {
        emit('update:modelValue', newValue);
      }
    });


    onBeforeUnmount(() => {
      editor.value.destroy();
    });

        return {
          editor,
          htmlMode,
          htmlContent,
          toggleHtmlMode,
          updateVisualContent,
          setLink,
          addImage: chooseImageSource, // Expose chooseImageSource as addImage
          fileInput, // Expose fileInput ref
          handleFileUpload, // Expose file handler
          uploadProgress, // Expose upload progress
          uploadMessage, // Expose upload message
        };
      },
    };    </script>

    <style scoped>
    .tiptap-editor {
      border: 1px solid #ddd;
      border-radius: 8px;
      position: relative; /* Added to make absolute positioning of children work */
    }
    .tiptap-toolbar {      background-color: #f0f0f0;
      padding: 5px;
      border-bottom: 1px solid #ddd;
      display: flex;
      flex-wrap: wrap;
      gap: 5px;
    }
    .tiptap-toolbar button {
      background: none;
      border: 1px solid #ccc;
      padding: 5px 10px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 0.9em;
    }
    .tiptap-toolbar button.is-active {
      background-color: #e91e63;
      color: white;
      border-color: #e91e63;
    }
    .tiptap-toolbar button:hover:not(.is-active) {
      background-color: #e0e0e0;
    }
    .tiptap-editor-content :deep(.ProseMirror) {
      min-height: 200px;
      padding: 10px;
      outline: none;
    }
    .tiptap-editor-content :deep(.ProseMirror p:first-child) {
      margin-top: 0;
    }
    .tiptap-editor-content :deep(img) {
      max-width: 100%;
      height: auto;
    }
    .tiptap-editor-content :deep(table) {
      border-collapse: collapse;
      width: 100%;
    }
    .tiptap-editor-content :deep(th),
    .tiptap-editor-content :deep(td) {
      border: 1px solid #ccc;
      padding: 5px 10px;
    }
    .html-source-editor {
      width: 100%;
      min-height: 200px;
      border: none;
      padding: 10px;
      font-family: monospace;
      white-space: pre-wrap;
      word-wrap: break-word;
      resize: vertical;
    }
        .toggle-html-mode {
          background-color: #007bff;
          color: white;
          border: none;
          padding: 5px 10px;
          border-radius: 4px;
          cursor: pointer;
          margin: 5px;
          position: absolute; /* Position absolutely within the editor container */
          top: 5px; /* Adjust as needed */
          right: 5px; /* Adjust as needed */
          z-index: 10; /* Ensure it's above the editor content */
        }
        .toggle-html-mode:hover {
          background-color: #0056b3;
        }
    </style>